import {
  AnalysisWorkerWrapper, Paper, interpreters, languageProcessing
} from "yoastseo";

import ProgressBar from 'progressbar.js';

class Route66Seo {

  constructor(options) {

    const defaults = {
      worker: 'https://localhost:9999/worker.js',
      locale: 'en_GB',
    };

    this.options = Object.assign({}, defaults, options || {});
    this.options.slug = this.options.url;
    this.score = 0;
    this.previousScore = 0;
    this.results = [];

    this.readabilityScore = 0;
    this.previousReadabilityScore = 0;
    this.readabilityResults = [];

    this.elements = {
      score: document.getElementById('route66-seo-score'),
      badges: document.getElementsByClassName('route66-seo-score-badge'),
      preview: document.getElementById('route66-seo-preview'),
      results: document.getElementById('route66-seo-analysis'),
      readabilityScore: document.getElementById('route66-readability-score'),
      readabilityBadges: document.getElementsByClassName('route66-readability-score-badge'),
      readabilityResults: document.getElementById('route66-readability-analysis'),
    };

    // BC for badges
    if (this.elements.badges.length === 0) {
      this.elements.badges = document.querySelectorAll('#route66-seo-score-badge');
    }
    if (this.elements.readabilityBadges.length === 0) {
      this.elements.readabilityBadges = document.querySelectorAll('#route66-readability-score-badge');
    }

    this.initialize();
  }

  initialize() {

    // Worker
    this.createWorker();

    // Score circle
    this.createScoreCircle();

    // Measure element
    this.createMeasureElement();

  }

  createWorker() {

    const languages = ['ar', 'ca', 'cs', 'de', 'el', 'en', 'es', 'fa', 'fr', 'he', 'hu', 'id', 'it', 'ja', 'nb', 'nl', 'pl', 'pt', 'ru', 'sk', 'sv', 'tr'];

    let language = languageProcessing.getLanguage(this.options.locale);

    if (language && languages.indexOf(language) === -1) {
      language = '_default';
    }

    const workerUnwrapped = new Worker(new URL(this.options.worker, import.meta.url));
    workerUnwrapped.postMessage({ language });
    this.worker = new AnalysisWorkerWrapper(workerUnwrapped);

    const options = {
      contentAnalysisActive: true,
      keywordAnalysisActive: true,
      useCornerstone: false,
      useTaxonomy: false,
      locale: this.options.locale,
      logLevel: "ERROR",
    };

    if (this.options.i18n) {
      options.translations = this.options.i18n;
    }

    this.worker.initialize(options).then(() => {
      console.log('Route 66 SEO analysis worker initialized');
    }).catch((error) => {
      console.error('Route 66 SEO analysis worker failed:');
      console.error(error);
    });
  }

  createScoreCircle() {
    this.circle = new ProgressBar.Circle(this.elements.score, {
      color: '#bd362f',
      strokeWidth: 8,
      trailColor: '#eee',
      trailWidth: 8,
      duration: 1200,
      easing: 'easeInOut',
      text: {
        value: 0,
        style: {
          color: '#000'
        }
      },
      step: (state, circle, attachment) => {
        let value = Math.round(circle.value() * 100);
        circle.setText(value);
      }
    });

    if (!this.elements.readabilityScore) {
      return;
    }

    this.readabilityCircle = new ProgressBar.Circle(this.elements.readabilityScore, {
      color: '#bd362f',
      strokeWidth: 8,
      trailColor: '#eee',
      trailWidth: 8,
      duration: 1200,
      easing: 'easeInOut',
      text: {
        value: 0,
        style: {
          color: '#000'
        }
      },
      step: (state, circle, attachment) => {
        let value = Math.round(circle.value() * 100);
        circle.setText(value);
      }
    });
  }

  analyze(text = '', attributes = {}) {

    if (attributes.url) {
      attributes.slug = attributes.url;
      delete attributes.url;
    }

    attributes.titleWidth = this.measureWidth(attributes.title);

    if (this.options.locale) {
      attributes.locale = this.options.locale;
    }

    this.paper = new Paper(text, attributes);
    return this.worker.analyze(this.paper).then((results) => {

      this.previousScore = this.score;
      this.score = this.normalizeScore(results.result.seo[""].score);
      this.results = results.result.seo[""].results;
      this.results.sort((a, b) => {
        return a.score - b.score;
      });

      this.previousReadabilityScore = this.readabilityScore;
      this.readabilityScore = this.normalizeScore(results.result.readability.score);
      this.readabilityResults = results.result.readability.results;
      this.readabilityResults.sort((a, b) => {
        return a.score - b.score;
      });

      this.render();

    }).catch((error) => {
      console.error('An error occured while performing the analysis:');
      console.error(error);
    });

  }

  normalizeScore(score) {
    return Math.max(0, score);
  }

  render() {
    this.renderPreview();
    this.renderScore();
    this.renderResults();

    if (!this.elements.readabilityScore) {
      return;
    }

    this.renderReadabilityScore();
    this.renderReadabilityResults();
  }

  renderPreview() {

    if (!this.elements.preview) {
      return;
    }

    const title = this.paper.getTitle();
    const url = this.paper.getPermalink();
    let description = this.paper.getDescription();
    if (!description) {
      description = languageProcessing.stripHTMLTags(this.paper.getText());
    }
    description = description.substr(0, 320);

    const buffer = `<h4 class="route66-seo-preview-title">${title}</h4><div class="route66-seo-preview-url">${url}</div><div class="route66-seo-preview-description">${description}</div>`;
    this.elements.preview.innerHTML = buffer;
  }

  renderScore() {
    const color = this.getColorFromScore(this.score);
    this.circle.path.setAttribute('stroke', color);
    this.circle.animate(this.score / 100);

    for (let i = 0; i < this.elements.badges.length; i++) {
      const badge = this.elements.badges[i];
      badge.innerHTML = this.score;
      if (this.score > 1) {
        badge.setAttribute('style', `background-color: ${color}`);
      } else {
        badge.removeAttribute('style');
      }
    }
  }

  renderResults() {

    let buffer = '';

    for (let i = 0; i < this.results.length; i++) {

      let result = this.results[i];

      if (result.text) {
        result.rating = this.getRatingFromScore(result.score);
        result.icon = this.getIconFromRating(result.rating);
        buffer += `<div class="route66-seo-analysis-score route66-seo-analysis-score-${result.rating}"><span class="route66-seo-analysis-icon ${result.icon}"></span><span class="route66-seo-analysis-text">${result.text}</span></div>`;
      }

    }

    this.elements.results.innerHTML = buffer;
  }

  renderReadabilityScore() {
    const color = this.getColorFromScore(this.readabilityScore);
    this.readabilityCircle.path.setAttribute('stroke', color);
    this.readabilityCircle.animate(this.readabilityScore / 100);


    for (let i = 0; i < this.elements.readabilityBadges.length; i++) {
      const badge = this.elements.readabilityBadges[i];
      badge.innerHTML = this.readabilityScore;
      if (this.readabilityScore > 1) {
        badge.setAttribute('style', `background-color: ${color}`);
      } else {
        badge.removeAttribute('style');
      }
    }

  }

  renderReadabilityResults() {

    let buffer = '';

    for (let i = 0; i < this.readabilityResults.length; i++) {

      let result = this.readabilityResults[i];

      if (result.text) {
        result.rating = this.getRatingFromScore(result.score);
        result.icon = this.getIconFromRating(result.rating);
        buffer += `<div class="route66-seo-analysis-score route66-seo-analysis-score-${result.rating}"><span class="route66-seo-analysis-icon ${result.icon}"></span><span class="route66-seo-analysis-text">${result.text}</span></div>`;
      }

    }

    this.elements.readabilityResults.innerHTML = buffer;
  }

  createMeasureElement() {
    this.measureElement = document.createElement('div');
    this.measureElement.id = 'route66-yoast-measurement-element';
    this.measureElement.style.position = 'absolute';
    this.measureElement.style.left = '-9999em';
    this.measureElement.style.top = 0;
    this.measureElement.style.height = 0;
    this.measureElement.style.overflow = 'hidden';
    this.measureElement.style.fontFamily = 'Arial';
    this.measureElement.style.fontSize = '18px';
    this.measureElement.style.fontWeight = '400';
    document.body.appendChild(this.measureElement);
  }

  measureWidth(text) {
    this.measureElement.innerHTML = text;
    return this.measureElement.offsetWidth;
  }

  getRatingFromScore(score) {
    return interpreters.scoreToRating(score);
  }

  getIconFromRating(rating) {

    let icon = '';

    if (rating === 'good') {
      icon = 'icon-ok';
    } else if (rating === 'ok') {
      icon = 'icon-warning';
    } else {
      icon = 'icon-not-ok';
    }

    return icon;
  }

  getColorFromScore(score) {

    let color = '';

    if (score > 70) {
      color = '#46a546';
    } else if (score > 40) {
      color = '#c67605';
    } else {
      color = '#bd362f';
    }

    return color;
  }

  setLocale(locale) {
    this.options.locale = locale;
    this.createWorker();
  }

}

window.Route66Seo = Route66Seo;

export {
  Route66Seo as
    default
};